import { AvField, AvForm } from "availity-reactstrap-validation";
import { withNamespaces } from "react-i18next";
import { CardHeader, Col, Label, Row } from "reactstrap";
import ButtonsGroup from "../ButtonsGroup";
import GeoMap from "src/modules/3rd-party-management/components/geo-map";
import { useCallback, useMemo, useState } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import CommonService from "src/modules/3rd-party-management/apis/common/CommonService";
import { toast } from "react-toastify";
import SupplierProductService from "src/modules/3rd-party-management/apis/common/SupplierProductService";

const GeoLocation = ({ t, onValidSubmit, onBackClicked, prevData, products }) => {
    const [geoLocationData, setGeoLocationData] = useState({
        geolocationReference: [],
        farmGeolocationReference: [],
        slaughterGeolocationReference: []
    });

    const originCountry = products[0].origin_country; // Assuming all products have the same origin country
    const originCountryId = originCountry.id;
    const originCountryName = originCountry.name;

    const commodity = (products[0].commodity)?.toLowerCase(); // Assuming all products have the same commodity   
    const importDate = products[0].import_date?.split(' ')[0]; // Assuming all products have the same import date

    const {
        data: originCountryGeoData,
        isFetching: originCountryGeoDataIsFetching
    } = useQuery({
        queryKey: [
            '3rd-party-management-fetch-country-geo-data',
            originCountryId
        ],
        queryFn: async () => {
            const service = CommonService.getInstance();

            return await service.fetchCountryGeoData(originCountryId);
        },
        cacheTime: 0,
        refetchOnWindowFocus: false,
        enabled: true,
        onError: () => {
            toast(t('Failed to load geolocation data for the selected country.'), {
                type: 'error'
            });
        }
    });

    const {
        mutateAsync: validatePlotMutation
    } = useMutation({
        mutationFn: async (payload) => {
            const service = SupplierProductService.getInstance();
            return await service.validateGeoLocation(payload);
        }
    });

    const plotValidator = useCallback(({ geoJSON }) => {
        return validatePlotMutation({
            ...geoJSON,
            properties: {
                commodity: commodity,
                name: `company-test-${commodity}`,
                production_end: importDate
            }
        });
    }, [validatePlotMutation]);

    const plotValidationSettings = useMemo(() => {
        return {
            enabled: true,
            validate: plotValidator,
            errorMessage: {
                title: t('Invalid Plot Detected'),
                body: (
                    <>
                        {t('The selected plot did not pass the plausibility verification check.')}
                        <br />
                        {t('This means the plot’s boundaries or geolocation data are not consistent with the expected agricultural area or satellite validation criteria.')}
                        <br />
                        {t('Please review the drawn area and ensure it accurately represents a valid production plot before proceeding.')}
                    </>
                )
            }
        }
    }, [plotValidator, t]);

    const boundaryRestrictionSettings = useMemo(() => {
        return {
            enabled: !!originCountryGeoData?.geojson,
            area: originCountryGeoData?.geojson,
            errorMessage: {
                title: t('Outside Country Boundary'),
                body: (
                    <>
                        {t('The selected area is outside the boundary of the chosen country.')}
                        <br />
                        {t('Please draw your plot within the country limits to ensure valid geolocation data.')}
                    </>
                )
            }
        }
    }, [t, originCountryGeoData?.geojson]);

    const handleAreaChange = useCallback(({ action, id, geoJSON, name }) => {
        switch (action) {
            case 'create':
                setGeoLocationData((currentState) => {
                    return {
                        ...currentState,
                        [name]: [
                            ...currentState[name],
                            {
                                id: id,
                                geoJSON: geoJSON
                            }
                        ]
                    }
                })
                break;

            case 'edit':
                setGeoLocationData((currentState) => {
                    const foundIndex = currentState[name].findIndex((item) => item.id === id);

                    const newState = { ...currentState };

                    if (foundIndex < 0) {
                        return newState;
                    }

                    newState[name][foundIndex].geoJSON = geoJSON;

                    return newState;
                })
                break;

            case 'delete':
                setGeoLocationData((currentState) => {
                    const newState = { ...currentState };

                    newState[name] = currentState[name].filter((item) => item.id !== id);

                    return newState;
                })
                break;
            default:
                break;
        }
    }, []);

    const handleMapLoad = useCallback((loadedItems, name) => {
        setGeoLocationData((currentState) => {
            const newState = { ...currentState };

            newState[name] = loadedItems;

            return newState;
        });
    }, []);

    return (
        <AvForm 
            onValidSubmit={(_e, values) => {
                onValidSubmit({
                    ...values,
                    geolocationReference: values.geolocationReference.map((geoItem) => {
                        return geoItem.geoJSON
                    }),
                    farmGeolocationReference: values.farmGeolocationReference?.map((loc) => loc.geoJSON),
                    slaughterGeolocationReference: values.slaughterGeolocationReference?.map((loc) => loc.geoJSON)
                })
            }} 
            className="needs-validation"
        >
            {commodity !== 'cattle' ? (
                <Row>
                    <Col sm='12'>
                        <Label>
                            {t('Geolocation (Draw Polygon)')} <span className="text-danger">*</span>
                        </Label>

                        <GeoMap
                            name="geolocationReference"
                            onAreaChange={handleAreaChange}
                            onLoaded={handleMapLoad}
                            preDefinedGeo={prevData?.geolocation_reference}
                            multiDraw={true}
                            defaultSearchParam={originCountryName}
                            boundaryRestriction={boundaryRestrictionSettings}
                            isLoading={originCountryGeoDataIsFetching}
                            plotValidation={plotValidationSettings}
                            styles={{
                                height: '500px'
                            }}
                        />

                        <AvField 
                            type='hidden'
                            name='geolocationReference'
                            validate={{
                                required: { value: true }
                            }}
                            value={geoLocationData?.geolocationReference}
                        />
                    </Col>
                </Row>
            ) : (
                <>
                <CardHeader className="bg-white mb-3 p-0">
                    <p className="text-muted mb-0">
                        {t('For cattle products, geolocation data must cover the entire production chain — from the animal’s birth to processing.')}{' '}
                        {t('Please draw the locations for the Breeding Farm, Fattening Farm, and Slaughterhouse on the maps below. Each map should accurately represent the area of activity to ensure full traceability and EUDR compliance.')}
                    </p>
                </CardHeader>
                <Row>
                    <Col sm='12' className="mb-4">
                        <Label>
                            {t('Breeding Farm Location')} <span className="text-danger">*</span>
                        </Label>

                        <GeoMap
                            name="geolocationReference"
                            onAreaChange={handleAreaChange}
                            onLoaded={handleMapLoad}
                            preDefinedGeo={prevData?.geolocation_reference}
                            multiDraw={true}
                            defaultSearchParam={originCountryName}
                            boundaryRestriction={boundaryRestrictionSettings}
                            isLoading={originCountryGeoDataIsFetching}
                            plotValidation={plotValidationSettings}
                            styles={{
                                height: '500px'
                            }}
                        />

                        <AvField 
                            type='hidden'
                            name='geolocationReference'
                            validate={{
                                required: { value: true }
                            }}
                            value={geoLocationData.geolocationReference}
                        />
                    </Col>

                    <Col sm='12' className="mb-4">
                        <Label>
                            {t('Fattening Farm Location')} <span className="text-danger">*</span>
                        </Label>

                        <GeoMap
                            name="farmGeolocationReference"
                            onAreaChange={handleAreaChange}
                            onLoaded={handleMapLoad}
                            preDefinedGeo={prevData?.farm_geolocation_reference}
                            multiDraw={true}
                            defaultSearchParam={originCountryName}
                            boundaryRestriction={boundaryRestrictionSettings}
                            isLoading={originCountryGeoDataIsFetching}
                            plotValidation={plotValidationSettings}
                            styles={{
                                height: '500px'
                            }}
                        />

                        <AvField 
                            type='hidden'
                            name='farmGeolocationReference'
                            validate={{
                                required: { value: true }
                            }}
                            value={geoLocationData.farmGeolocationReference}
                        />
                    </Col>

                    <Col sm='12' className="mb-4">
                        <Label>
                            {t('Slaughterhouse Location')} <span className="text-danger">*</span>
                        </Label>

                        <GeoMap
                            name="slaughterGeolocationReference"
                            onAreaChange={handleAreaChange}
                            onLoaded={handleMapLoad}
                            preDefinedGeo={prevData?.slaughter_geolocation_reference}
                            multiDraw={true}
                            defaultSearchParam={originCountryName}
                            boundaryRestriction={boundaryRestrictionSettings}
                            isLoading={originCountryGeoDataIsFetching}
                            plotValidation={plotValidationSettings}
                            styles={{
                                height: '500px'
                            }}
                        />

                        <AvField 
                            type='hidden'
                            name='slaughterGeolocationReference'
                            validate={{
                                required: { value: true }
                            }}
                            value={geoLocationData?.slaughterGeolocationReference}
                        />
                    </Col>
                </Row>  
                </>
            )}

            <ButtonsGroup
                onBackClicked={onBackClicked}
                onNextClicked={() => { }}
                nextButtonType='submit'
            />
        </AvForm>
    )
};

export default withNamespaces()(
    GeoLocation
);